import { useState, useRef, useEffect } from 'react';
import { useConversation } from '../../contexts/ConversationContext';

export default function SaveViewButton() {
  const { currentA2UI, saveView } = useConversation();
  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState('');
  const [saveSuccess, setSaveSuccess] = useState(false);
  const formRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Close form when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (formRef.current && !formRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setName('');
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus input when opening
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSave = () => {
    if (!name.trim()) return;

    saveView(name.trim());
    setSaveSuccess(true);
    setIsOpen(false);
    setName('');

    setTimeout(() => setSaveSuccess(false), 2000);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setName('');
    }
  };

  // Don't render if no visualization to save
  if (!currentA2UI) return null;

  return (
    <div className="relative" ref={formRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-1.5 text-xs font-mono uppercase tracking-wider text-grey-medium hover:text-black transition-colors"
        aria-label="Save view"
      >
        {/* Bookmark icon (Solar style) */}
        <svg
          className="w-4 h-4"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={1}
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"
          />
        </svg>
        {saveSuccess ? 'Saved!' : 'Save'}
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-1 w-56 bg-white border border-grey-medium/30 shadow-sm p-3" style={{ zIndex: 500 }}>
          <p className="font-mono text-[10px] text-grey-medium uppercase tracking-wider mb-2">
            Save View
          </p>
          <input
            ref={inputRef}
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Enter name..."
            className="w-full px-2 py-1.5 text-sm font-light border border-grey-medium/30 focus:border-mint focus:outline-none bg-white mb-2"
          />
          <button
            onClick={handleSave}
            disabled={!name.trim()}
            className="w-full py-1.5 text-xs font-mono uppercase tracking-wider bg-black text-white disabled:bg-grey-medium disabled:cursor-not-allowed hover:bg-black/90 active:scale-[0.98] transition-all"
          >
            Save
          </button>
        </div>
      )}
    </div>
  );
}
